/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import { IconProvider } from '../icon/context';
import styles from './styles.module.css';
import type { ComponentPropsWithRef } from 'react';

/**
 * HotkeySet - Container for grouping multiple Hotkey components.
 *
 * Automatically sizes icons via IconProvider.
 *
 * @param props - ComponentPropsWithRef<'div'>
 * @param props.className - Optional CSS class name.
 * @returns The rendered HotkeySet component.
 *
 * @example
 * ```tsx
 * <HotkeySet>
 *   <Hotkey>CMD</Hotkey>
 *   <span>+</span>
 *   <Hotkey>K</Hotkey>
 * </HotkeySet>
 * ```
 */
export function HotkeySet({
  className,
  ...rest
}: ComponentPropsWithRef<'div'>) {
  return (
    <IconProvider size='large'>
      <div {...rest} className={clsx(styles.set, className)} />
    </IconProvider>
  );
}
